import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

export default function AdminTest() {
  const [prompts, setPrompts] = useState({});
  const [selected, setSelected] = useState('');
  const [promptText, setPromptText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regenCtx, setRegenCtx] = useState({
    topic: '',
    research_area: '',
    previous_content: '',
    context: '',
  });
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/admin/prompts');
        setPrompts(res.prompts || {});
        const first = Object.keys(res.prompts || {})[0];
        if (first) {
          setSelected(first);
          setPromptText(res.prompts[first]);
        }
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load prompts');
      }
    })();
  }, []);

  const promptNames = useMemo(() => Object.keys(prompts).sort(), [prompts]);
  const isSectionPrompt = selected.startsWith('SECTION_');

  function choose(name) {
    setSelected(name);
    setPromptText(prompts[name] || '');
    setResult(null);
    setErr(null);
    setFlash(null);
  }

  async function run() {
    setErr(null);
    setFlash(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await api.post(
        '/api/admin/test-prompt',
        { prompt: promptText, user_input: userInput },
      );
      if (res.success) {
        setResult(res.result);
        if (res.warning) setFlash(res.warning);
      } else {
        setErr('Test failed');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Test failed');
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (!isSectionPrompt) return;
    if (!regenCtx.topic || !regenCtx.research_area) {
      setErr('Topic and research area are required to regenerate.');
      return;
    }
    setErr(null);
    setRegenBusy(true);
    try {
      const res = await api.post(
        '/api/admin/regenerate-prompt',
        { prompt_name: selected, ...regenCtx },
      );
      if (res.prompt) setPromptText(res.prompt);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Regenerate failed');
    } finally {
      setRegenBusy(false);
    }
  }

  async function savePrompt() {
    setErr(null);
    setFlash(null);
    try {
      await api.post('/api/admin/update-prompt', {
        prompt_name: selected,
        new_content: promptText,
      });
      setPrompts((prev) => ({ ...prev, [selected]: promptText }));
      setFlash('Saved to session (not persisted to file).');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    }
  }

  return (
    <div className="main-container">
      <Link to="/admin" className="text-muted small">
        <i className="fas fa-arrow-left me-1"></i>Back to admin
      </Link>
      <div className="mt-2 mb-4">
        <h1 style={{ fontWeight: 700 }}>
          <i className="fas fa-vial me-2" style={{ color: 'var(--primary)' }}></i>
          Prompt testing
        </h1>
        <p className="text-muted mb-0">
          Select a prompt, optionally edit, and test with the writing model.
        </p>
      </div>

      <div className="row g-3">
        <div className="col-lg-3">
          <div className="card" style={{ maxHeight: 640, overflowY: 'auto' }}>
            <div className="card-body">
              <h6 className="text-muted text-uppercase small mb-3" style={{ letterSpacing: 2 }}>
                Prompts
              </h6>
              <div className="d-flex flex-column gap-1">
                {promptNames.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className={`btn btn-sm text-start ${
                      name === selected ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
                    onClick={() => choose(name)}
                    style={{ fontSize: 12 }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-9">
          <div className="d-flex flex-column gap-3">
            {isSectionPrompt && (
              <div className="card">
                <div className="card-body">
                  <h6 className="fw-semibold mb-3">
                    <i className="fas fa-sync-alt me-2" style={{ color: 'var(--primary)' }}></i>
                    Regenerate with context
                  </h6>
                  <div className="row g-2 mb-2">
                    <div className="col-md-6">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Topic"
                        value={regenCtx.topic}
                        onChange={(e) => setRegenCtx({ ...regenCtx, topic: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Research area"
                        value={regenCtx.research_area}
                        onChange={(e) =>
                          setRegenCtx({ ...regenCtx, research_area: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <textarea
                    className="form-control form-control-sm mb-2"
                    rows={2}
                    placeholder="Previous content (optional)"
                    value={regenCtx.previous_content}
                    onChange={(e) =>
                      setRegenCtx({ ...regenCtx, previous_content: e.target.value })
                    }
                  />
                  <textarea
                    className="form-control form-control-sm mb-2"
                    rows={2}
                    placeholder="Extra context (optional)"
                    value={regenCtx.context}
                    onChange={(e) => setRegenCtx({ ...regenCtx, context: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={regenerate}
                    disabled={regenBusy}
                  >
                    <i className="fas fa-magic me-1"></i>
                    {regenBusy ? 'Regenerating…' : 'Regenerate prompt'}
                  </button>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-body">
                <label className="form-label fw-semibold">Prompt</label>
                <textarea
                  className="form-control"
                  rows={14}
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  style={{ fontFamily: 'Consolas, Monaco, monospace', fontSize: 12 }}
                />

                <label className="form-label fw-semibold mt-3">Optional user input</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                />

                <div className="d-flex gap-2 flex-wrap mt-3">
                  <button
                    type="button"
                    className="btn btn-cta"
                    onClick={run}
                    disabled={busy || !promptText.trim()}
                  >
                    <i className="fas fa-play me-1"></i>
                    {busy ? 'Testing…' : 'Test prompt'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={savePrompt}
                  >
                    <i className="fas fa-save me-1"></i>Save edits (session)
                  </button>
                </div>
              </div>
            </div>

            {err && <div className="alert alert-danger mb-0">{err}</div>}
            {flash && <div className="alert alert-info mb-0">{flash}</div>}

            {result && (
              <div className="card">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase small mb-3" style={{ letterSpacing: 2 }}>
                    Result
                  </h6>
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      maxHeight: 440,
                      overflow: 'auto',
                      fontFamily: 'Consolas, Monaco, monospace',
                      fontSize: 13,
                      background: 'var(--light)',
                      borderRadius: 8,
                      padding: '1rem',
                    }}
                  >
                    {result}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
